// Centralized mock service to handle UI interactions (upload, save, delete, etc.)
// This serves as the functional backend for the MVP until real APIs are connected.

export interface MockResponse {
  success: boolean;
  message: string;
  data?: unknown;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
// Mock persistent storage
const tickets: unknown[] = [];
const users: unknown[] = [];

// Placeholder for referral/contribution bonus calculation
const calculateBonuses = (data: unknown): number => {
  let creditBonus = 0;

  // Logic: 10% bonus for waitlist referral usage
  if (typeof data === "object" && data !== null && "waitlistCode" in data) {
    creditBonus += 10;
  }

  return creditBonus;
};

export const mockService = {
  // ... existing methods
  registerUser: async (userData: unknown): Promise<MockResponse> => {
    // Check for duplicates
    const isTaken = users.some(
      (u) =>
        typeof u === "object" &&
        u !== null &&
        typeof (u as Record<string, unknown>).username === "string" &&
        typeof (u as Record<string, unknown>).email === "string" &&
        ((u as Record<string, unknown>).username ===
          (userData as Record<string, unknown>).username ||
          (u as Record<string, unknown>).email ===
            (userData as Record<string, unknown>).email),
    );
    if (isTaken) {
      return { success: false, message: "Username or Email already taken." };
    }

    // Validate Department uniqueness for Course Reps
    if (
      typeof userData === "object" &&
      userData !== null &&
      (userData as Record<string, unknown>).role === "course_rep"
    ) {
      const department =
        (userData as Record<string, unknown>).department === "Other"
          ? ((userData as Record<string, unknown>).otherDepartment as string)
          : ((userData as Record<string, unknown>).department as string);
      const existingRep = users.find(
        (u) =>
          typeof u === "object" &&
          u !== null &&
          (u as Record<string, unknown>).role === "course_rep" &&
          ((u as Record<string, unknown>).department === department ||
            ((u as Record<string, unknown>).department === "Other" &&
              (u as Record<string, unknown>).otherDepartment === department)),
      );

      if (existingRep) {
        return {
          success: false,
          message: `A Course Representative already exists for the ${department} department.`,
        };
      }
    }

    console.log("Registering user:", userData);
    const bonus = calculateBonuses(userData);
    const user = {
      ...(userData as Record<string, unknown>),
      id: Date.now(),
      creditBonus: bonus,
      status:
        (userData as Record<string, unknown>).role === "course_rep"
          ? "pending_verification"
          : "active",
    };
    users.push(user);
    await delay(1000);
    return {
      success: true,
      message: "User registered successfully.",
      data: user,
    };
  },
  // ...

  upload: async (file: File): Promise<MockResponse> => {
    console.log("Uploading file:", file.name);
    await delay(1000);
    return {
      success: true,
      message: `File ${file.name} uploaded successfully.`,
    };
  },

  save: async (data: unknown): Promise<MockResponse> => {
    console.log("Saving data:", data);
    await delay(800);

    // Support ticket persistence
    if (
      typeof data === "object" &&
      data !== null &&
      (data as Record<string, unknown>).action === "submit_support_ticket"
    ) {
      tickets.push({ ...(data as Record<string, unknown>), id: Date.now() });
    }

    return { success: true, message: "Data saved successfully.", data };
  },

  delete: async (id: string): Promise<MockResponse> => {
    console.log("Deleting item:", id);
    await delay(800);
    return { success: true, message: `Item ${id} deleted successfully.` };
  },

  update: async (id: string, updates: unknown): Promise<MockResponse> => {
    console.log("Updating item:", id, updates);
    await delay(800);
    return { success: true, message: `Item ${id} updated successfully.` };
  },

  getTickets: async (): Promise<unknown[]> => {
    await delay(500);
    return tickets;
  },
};
